import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  addDoc, 
  serverTimestamp,
  updateDoc,
  increment
} from 'firebase/firestore';
import { Link, useLocation } from 'react-router-dom';
import './DesignPage.css';
import ditherArizona from '../assets/images/dither-arizona.png';

const DesignPage = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [userMonthlyDesign, setUserMonthlyDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // Check if user has submitted this month
  const hasSubmittedThisMonth = () => {
    if (!userMonthlyDesign) {
      return false;
    }
    
    if (!userMonthlyDesign.createdAt) {
      return false;
    }
    
    let designDate;
    try {
      // Handle different timestamp formats
      if (userMonthlyDesign.createdAt.toDate) {
        designDate = userMonthlyDesign.createdAt.toDate();
      } else if (userMonthlyDesign.createdAt.seconds) {
        designDate = new Date(userMonthlyDesign.createdAt.seconds * 1000);
      } else if (userMonthlyDesign.createdAt instanceof Date) {
        designDate = userMonthlyDesign.createdAt;
      } else {
        return false;
      }
      
      const now = new Date();
      const isSameMonth = designDate.getMonth() === now.getMonth() && 
                         designDate.getFullYear() === now.getFullYear();
      
      return isSameMonth;
    } catch (error) {
      console.error('Error processing timestamp in hasSubmittedThisMonth:', error);
      return false;
    }
  };

  // Get current month name
  const getCurrentMonthName = () => {
    return new Date().toLocaleString('default', { month: 'long' });
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserData();
      fetchUserMonthlyDesign();
    }
  }, [currentUser]);


  const fetchUserData = async () => {
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        setUserData(userDocSnap.data());
      }
    } catch (error) {
      console.error("Error fetching user data: ", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserMonthlyDesign = async () => {
    try {
      const currentMonth = new Date();
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const designsRef = collection(db, 'designs');
      const q = query(
        designsRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Find the most recent design from this month
        let monthlyDesign = null;
        for (const doc of querySnapshot.docs) {
          const designData = doc.data();
          
          if (designData.createdAt) {
            let designDate;
            try {
              // Handle different timestamp formats
              if (designData.createdAt.toDate) {
                designDate = designData.createdAt.toDate();
              } else if (designData.createdAt.seconds) {
                designDate = new Date(designData.createdAt.seconds * 1000);
              } else if (designData.createdAt instanceof Date) {
                designDate = designData.createdAt;
              } else {
                continue;
              }
              
              const isThisMonth = designDate.getMonth() === currentMonth.getMonth() && 
                                 designDate.getFullYear() === currentMonth.getFullYear();
              
              if (isThisMonth) {
                // Fetch the environment photo URL if it exists
                let environmentPhotoUrl = null;
                if (designData.environmentPhotoId) {
                  try {
                    // You might need to adjust this based on your storage structure
                    // This assumes you store the environment photo URL in the design document
                    environmentPhotoUrl = designData.environmentPhotoUrl || designData.environmentPhotoId;
                  } catch (error) {
                    console.error('Error fetching environment photo:', error);
                  }
                }
                
                monthlyDesign = {
                  id: doc.id,
                  ...designData,
                  environmentPhotoUrl: environmentPhotoUrl
                };
                
                // Debug logging
                console.log('Monthly design data fetched:', {
                  id: monthlyDesign.id,
                  imageUrl: monthlyDesign.imageUrl,
                  hasEdits: monthlyDesign.hasEdits,
                  finalDesignChoice: monthlyDesign.finalDesignChoice,
                  editedImageUrl: monthlyDesign.editedImageUrl,
                  selectedVariant: monthlyDesign.selectedVariant,
                  fullData: monthlyDesign
                });
                
                break;
              }
            } catch (error) {
              console.error('Error processing timestamp for design:', doc.id, error);
              continue;
            }
          }
        }
        
        if (monthlyDesign) {
          setUserMonthlyDesign(monthlyDesign);
        } else {
          setUserMonthlyDesign(null);
        }
      } else {
        setUserMonthlyDesign(null);
      }
    } catch (error) {
      console.error("Error fetching user monthly design: ", error);
      setUserMonthlyDesign(null);
    }
  };



  const DesignCard = ({ design, isMonthly = false }) => {
    const cardClass = isMonthly ? 'design-card' : 'consistent-design-card';
    
    // Debug logging
    console.log('DesignCard received design data:', {
      id: design.id,
      imageUrl: design.imageUrl,
      hasEdits: design.hasEdits,
      finalDesignChoice: design.finalDesignChoice,
      editedImageUrl: design.editedImageUrl,
      selectedVariant: design.selectedVariant
    });
    
    const handleDesignerClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Navigate to designer profile
              window.location.href = `/gallery/user/${design.userId}`;
    };
    
    return (
              <Link to={`/gallery/${design.id}`} className={cardClass}>
        <div className="design-image">
          <img 
            src={design.imageUrl} 
            alt={design.name}
            className="camo-image"
            loading="lazy"
          />
          {isMonthly && design.environmentPhotoUrl && (
            <img 
              src={design.environmentPhotoUrl} 
              alt="Environment photo"
              className="environment-image"
              loading="lazy"
            />
          )}
        </div>
        <div className="design-info">
          <h4 className="design-name">{design.name || 'No Name'}</h4>
          <p className="designer-name">
            by{' '}
            <button
              className="designer-link"
              onClick={handleDesignerClick}
            >
              {design.designerName}
            </button>
          </p>
          {design.county && design.state && (
            <p className="design-location">
              {design.county}, {design.state}
            </p>
          )}
          <p className="design-date">
            {design.createdAt?.toDate().toLocaleDateString()}
          </p>
          <div className="design-stats">
            <span className="vote-count">❤️ {design.voteCount || 0}</span>
          </div>
        </div>
      </Link>
    );
  };

  if (loading) {
    return <div className="design-page-loading">Loading...</div>;
  }

  return (
    <div className="design-page">
      <div className="page-header">
        <h1>Design Studio</h1>
        <p>Create and submit your monthly camo patterns</p>
      </div>

      {message && <div className="message">{message}</div>}

      {/* User's Monthly Submission Section */}
      <section className="monthly-submission-section">
        <h2>{getCurrentMonthName()} Submission</h2>
        {hasSubmittedThisMonth() ? (
          <div className="user-monthly-design">
            {userMonthlyDesign && userMonthlyDesign.imageUrl ? (
              <DesignCard design={userMonthlyDesign} isMonthly={true} />
            ) : (
              <div className="design-placeholder">
                <p>Design submitted but image not available</p>
                <p>Design ID: {userMonthlyDesign?.id}</p>
                <p>Debug info:</p>
                <pre>{JSON.stringify({
                  imageUrl: userMonthlyDesign?.imageUrl,
                  hasEdits: userMonthlyDesign?.hasEdits,
                  finalDesignChoice: userMonthlyDesign?.finalDesignChoice,
                  editedImageUrl: userMonthlyDesign?.editedImageUrl,
                  selectedVariant: userMonthlyDesign?.selectedVariant
                }, null, 2)}</pre>
              </div>
            )}
            <div className="submission-actions">
              <Link to="/create-design" className="create-another-btn">
                Create Another Design
              </Link>
            </div>
          </div>
        ) : (
          <div 
            className="create-prompt"
            style={{ backgroundImage: `url(${ditherArizona})` }}
          >
            <h3>Create Your Monthly Design</h3>
            <p>You haven't submitted a design for {getCurrentMonthName()} yet.</p>
            <Link to="/create-design" className="create-design-btn">
              Design
            </Link>
          </div>
        )}
      </section>

      {/* Design Tools & Resources Section */}
      <section className="design-tools-section">
        <h2>Design Tools & Resources</h2>
        <div className="tools-grid">
          <div className="design-guidelines-section">
            <h3>How It Works</h3>
            <div className="guidelines-content">
              <div className="guideline-item">
                <h4>1. Upload Photo</h4>
                <p>Upload a photo of your environment that you want the camo pattern to blend with.</p>
              </div>
              <div className="guideline-item">
                <h4>2. Add Description</h4>
                <p>Provide a short description that will give additional guidance for the system to understand your vision.</p>
              </div>
              <div className="guideline-item">
                <h4>3. Location Details</h4>
                <p>Provide the state and county so we know where this camo belongs and can create region-specific patterns.</p>
              </div>
              <div className="guideline-item">
                <h4>4. Select Features</h4>
                <p>Choose which features you want to include in your camo pattern design.</p>
              </div>
              <div className="guideline-item">
                <h4>5. AI Generation</h4>
                <p>Let our system handle the rest! You'll receive two unique designs to choose from.</p>
              </div>
              <div className="guideline-item">
                <h4>6. Choose & Name</h4>
                <p>Select your favorite design, give it a name, and share it with the community. Everyone will be able to see your creation!</p>
              </div>
            </div>
          </div>
          <div className="tool-card">
            <h3>Inspiration Gallery</h3>
            <p>Browse the community gallery for inspiration and ideas. See what others have created and get inspired for your own designs.</p>
            <Link to="/gallery" className="tool-link">Browse Gallery</Link>
          </div>
          <div className="tool-card">
            <h3>Design History</h3>
            <p>View your previous submissions and track your progress. See how your designs have evolved over time and revisit your favorites.</p>
            <Link to="/profile" className="tool-link">View History</Link>
          </div>
          <div className="tool-card">
            <h3>Ownership Policy</h3>
            <p>All generated camouflage patterns become our exclusive intellectual property. By submitting content, you assign all rights.</p>
            <Link to="/terms" className="tool-link">Read Full Terms</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignPage; 